import React from 'react';
import { Box, Typography } from '@mui/material';

const HEIGHT = 170;
const BAR_WIDTH = 46;
const GAP = 14;
const MIN_BAR = 8;
const VALUE_ROOM = 18; // px reserved above a full bar for its value label

/**
 * VerticalBars — a column bar chart (one bar per entity), scrolling
 * horizontally when there are more bars than fit so it never overflows the
 * card. Draws an optional dashed average line across the chart and, when
 * `onSelect` is given, lets a bar be tapped (dimming the others).
 * `items: [{ label, value, color, data }]`; `formatValue` renders the value
 * label (e.g. two-decimal percentages). Bars scale to the largest value.
 */
const VerticalBars = function VerticalBars({
  items = [],
  formatValue = (v) => v,
  averageValue = null,
  averageLabel = null,
  onSelect = null,
  selectedLabel = null,
}) {
  const max = Math.max(1, ...items.map((i) => i.value || 0));
  const barPixels = (value) =>
    Math.round(((value || 0) / max) * (HEIGHT - VALUE_ROOM));
  const avgTop =
    averageValue != null
      ? HEIGHT - barPixels(Math.min(max, Math.max(0, averageValue)))
      : null;

  return (
    <Box className="scr" sx={{ overflowX: 'auto', pb: 0.5 }}>
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'flex-end',
          gap: `${GAP}px`,
          minWidth: '100%',
          px: 0.5,
        }}
      >
        {avgTop != null ? (
          <>
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: avgTop,
                borderTop: '2px solid',
                borderColor: (theme) => theme.palette.graphs.curveLine,
                opacity: 0.7,
                pointerEvents: 'none',
              }}
            />
            {averageLabel ? (
              <Typography
                sx={{
                  position: 'absolute',
                  left: 2,
                  top: Math.max(0, avgTop - 15),
                  fontSize: 10,
                  fontWeight: 700,
                  color: (theme) => theme.palette.graphs.curveLine,
                  px: 0.5,
                  borderRadius: '4px',
                  pointerEvents: 'none',
                }}
              >
                {averageLabel}
              </Typography>
            ) : null}
          </>
        ) : null}

        {items.map((item) => {
          const dimmed = selectedLabel && item.label !== selectedLabel;
          return (
            <Box
              key={item.label}
              role={onSelect ? 'button' : undefined}
              onClick={
                onSelect
                  ? () =>
                      item.label === selectedLabel
                        ? onSelect(null)
                        : onSelect(item)
                  : undefined
              }
              sx={{
                width: BAR_WIDTH,
                flex: 'none',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: onSelect ? 'pointer' : 'default',
                opacity: dimmed ? 0.4 : 1,
                transition: 'opacity .15s',
              }}
            >
              <Box
                sx={{
                  height: HEIGHT,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#1a1a1f',
                    mb: 0.5,
                  }}
                  noWrap
                >
                  {formatValue(item.value)}
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    height: Math.max(MIN_BAR, barPixels(item.value)),
                    borderRadius: '8px 8px 0 0',
                    bgcolor: item.color,
                    outline:
                      selectedLabel === item.label
                        ? '2px solid rgba(29,91,157,.55)'
                        : 'none',
                    outlineOffset: '1px',
                  }}
                />
              </Box>
              <Typography
                title={item.label}
                sx={{
                  fontSize: 11,
                  color: '#94a3b8',
                  mt: 0.75,
                  width: BAR_WIDTH,
                  textAlign: 'center',
                }}
                noWrap
              >
                {item.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default VerticalBars;
